import React, { useCallback } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fireTrackAnalytics } from '@atlassian/analytics-bridge';
import { FormattedMessage } from '@atlassian/jira-intl';
import { useFormSaveErrorsActions } from '@atlassian/proforma-common-core/form-system';
import type { FormStore } from '@atlassian/proforma-common-core/form-system-stores';
import {
	useFormIndexActions,
	useSelectedFormStoreState,
	useWithDiscardChangeNotice,
	type FormIdentifier,
} from '@atlassian/proforma-form-system';
import { Action, ActionSubject } from '@atlassian/proforma-utils/analytics';
import { useShowNotice } from '@atlassian/proforma-utils/show-notice';
import { fg } from '@atlassian/jira-feature-gating';
import type { IssueViewStore } from '../../stores/issue-view-store.tsx';
import { messages } from './messages.tsx';

const getAnalyticsAttributes = (formId: number | undefined) => ({
	formId,
	action: formId ? 'select' : 'unselect',
});

/**
 * This hook returns a select form function
 * which does _not_ check formStore.answersModified and show notice
 *
 * @param loadFormStore
 * @param disableIssueViewFullPageDropzoneCallback
 * @returns a function to handle select form from form index
 */
export const useSelectFormNoDiscardChangeNotice = (
	loadFormStore: (formId: number) => Promise<FormStore>,
	disableIssueViewFullPageDropzoneCallback: (isFormInEditMode: boolean) => void,
) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { setSelectedFormId, setSelectedFormStoreState, reportError } = useFormIndexActions();
	const { clear: clearFormSaveErrors } = useFormSaveErrorsActions();

	return useCallback(
		async (formId?: number, preloadFormStore?: FormStore, hasAccess = true) => {
			try {
				setSelectedFormId(formId);
				disableIssueViewFullPageDropzoneCallback(false);
				clearFormSaveErrors();

				if (!formId) {
					// 1. Unselect all form
					setSelectedFormStoreState({
						loading: false,
						data: undefined,
						error: undefined,
					});
					fireTrackAnalytics(
						createAnalyticsEvent({}),
						`${ActionSubject.FORM} ${Action.SELECTED}`,
						getAnalyticsAttributes(formId),
					);
					return;
				}

				if (!hasAccess && fg('granular_permissions_on_forms_ga')) {
					// 2. When user has no form access, clear selected form store, UI would show warning message
					setSelectedFormStoreState({
						loading: false,
						data: undefined,
						error: undefined,
					});
					fireTrackAnalytics(
						createAnalyticsEvent({}),
						`${ActionSubject.FORM} ${Action.SELECTED}`,
						getAnalyticsAttributes(formId),
					);
					return;
				}

				if (!preloadFormStore) {
					// 3. Select and load form
					setSelectedFormStoreState({
						loading: true,
						data: undefined,
						error: undefined,
					});

					const formStore = await loadFormStore(formId);

					setSelectedFormStoreState({
						loading: false,
						data: formStore,
						error: undefined,
					});
				} else {
					// 4. Select and use preloaded formStore form
					setSelectedFormStoreState({
						loading: false,
						data: preloadFormStore,
						error: undefined,
					});
				}
				fireTrackAnalytics(
					createAnalyticsEvent({}),
					`${ActionSubject.FORM} ${Action.SELECTED}`,
					getAnalyticsAttributes(formId),
				);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				reportError(error, {
					id: 'selectForm',
					attributes: getAnalyticsAttributes(formId),
				});
				setSelectedFormStoreState({
					loading: false,
					data: undefined,
					error,
				});
			}
		},
		[
			setSelectedFormId,
			disableIssueViewFullPageDropzoneCallback,
			clearFormSaveErrors,
			createAnalyticsEvent,
			setSelectedFormStoreState,
			loadFormStore,
			reportError,
		],
	);
};

export const useHandleSelectForm = (
	loadFormStore: (formId: number) => Promise<FormStore>,
	disableIssueViewFullPageDropzoneCallback: (isFormInEditMode: boolean) => void,
) => {
	const selectFormNoDiscardChangeNotice = useSelectFormNoDiscardChangeNotice(
		loadFormStore,
		disableIssueViewFullPageDropzoneCallback,
	);
	const withDiscardChangeNotice = useWithDiscardChangeNotice();

	const handleSelectOld = useCallback(
		(formId?: number) => withDiscardChangeNotice(() => selectFormNoDiscardChangeNotice(formId)),
		[selectFormNoDiscardChangeNotice, withDiscardChangeNotice],
	);
	const handleSelect = useCallback(
		(formToSelect?: FormIdentifier) =>
			withDiscardChangeNotice(() =>
				selectFormNoDiscardChangeNotice(formToSelect?.formId, undefined, formToSelect?.hasAccess),
			),
		[selectFormNoDiscardChangeNotice, withDiscardChangeNotice],
	);

	return { handleSelect, handleSelectOld };
};

/**
 * @deprecated
 * @param issueViewStore
 * @param disableIssueViewFullPageDropzoneCallback
 * @returns
 */
export const useHandleFormSelected = (
	issueViewStore: IssueViewStore,
	disableIssueViewFullPageDropzoneCallback: (isFormInEditMode: boolean) => void,
) => {
	const { setSelectedFormId, setSelectedFormStoreState } = useFormIndexActions();
	const { selectedFormStore } = useSelectedFormStoreState();
	const { clear: clearFormSaveErrors } = useFormSaveErrorsActions();
	const { showNotice } = useShowNotice();

	const handleFormSelected = useCallback(
		async (formId?: number, loadForm = true) => {
			try {
				setSelectedFormId(formId);
				disableIssueViewFullPageDropzoneCallback(false);

				if (!formId) {
					setSelectedFormStoreState({
						loading: false,
						data: undefined,
						error: undefined,
					});
					return;
				}

				if (loadForm) {
					setSelectedFormStoreState({
						loading: true,
						data: undefined,
						error: undefined,
					});

					const formStore = await issueViewStore.loadFormStore(formId);

					setSelectedFormStoreState({
						loading: false,
						data: formStore,
						error: undefined,
					});

					clearFormSaveErrors();
				}
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				setSelectedFormStoreState({
					loading: false,
					data: undefined,
					error,
				});
			}
		},
		[
			setSelectedFormId,
			issueViewStore,
			disableIssueViewFullPageDropzoneCallback,
			setSelectedFormStoreState,
			clearFormSaveErrors,
		],
	);

	return useCallback(
		(formId?: number, loadForm = true) => {
			if (selectedFormStore?.answersModified) {
				showNotice({
					title: <FormattedMessage {...messages.confirmDiscardChangesNoticeTitle} />,
					body: (
						<FormattedMessage
							{...messages.confirmDiscardChangesNoticeBody}
							values={{ formName: selectedFormStore?.formName || 'Unknown form' }}
						/>
					),
					closeText: <FormattedMessage {...messages.noticeCloseLabel} />,
					confirmText: <FormattedMessage {...messages.noticeConfirmLabel} />,
					onConfirmHandler: () => {
						handleFormSelected(formId, loadForm);
					},
				});
			} else {
				handleFormSelected(formId, loadForm);
			}
		},
		[
			handleFormSelected,
			selectedFormStore?.answersModified,
			selectedFormStore?.formName,
			showNotice,
		],
	);
};
